/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import { useCompanyPackageAccess } from "@/hooks/useCompanyPackageAccess";
import { useCompanyUsage } from "@/hooks/useCompanyUsage";
import { useParams, useNavigate } from "react-router-dom";
import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";
import { toast as sonnerToast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Save, Send, Eye, X, Plus, Image as ImageIcon, Globe, Check } from "lucide-react";
import MediaPickerDialog from "@/components/MediaPickerDialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { LANGUAGES, DEFAULT_LANGUAGE } from "@/lib/languages";

const tenantDb = supabase as any;

function slugify(text: string) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

const defaultArticle = {
  title: "",
  slug: "",
  subtitle: "",
  content: "",
  excerpt: "",
  featured_image_url: "",
  category_id: "",
  language: DEFAULT_LANGUAGE as string,
  tags: [] as string[],
  status: "approved" as string,
  is_breaking: false,
  is_featured: false,
  is_opinion: false,
  seo_title: "",
  seo_description: "",
};

export default function ArticleEditorPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentCompanyId } = useCompany();
  const { package: packageInfo, canCreateArticles } = useCompanyPackageAccess();
  const { usage } = useCompanyUsage();
  const { toast } = useToast();
  const isTrialPackage = Boolean(packageInfo?.is_trial);

  const [article, setArticle] = useState(defaultArticle);
  const [categories, setCategories] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [selectedWebsiteIds, setSelectedWebsiteIds] = useState<string[]>([]);
  const [websitePopoverOpen, setWebsitePopoverOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch websites for multi-select
  const { data: websites = [] } = useQuery({
    queryKey: ["websites-list", currentCompanyId],
    queryFn: async () => {
      const { data } = await tenantDb.from("websites").select("id, name, domain").eq("company_id", currentCompanyId).order("name");
      return data || [];
    },
    enabled: !!currentCompanyId,
  });

  // Fetch existing publications for this article
  useEffect(() => {
    if (!isNew && id) {
      supabase
        .from("article_publications")
        .select("website_id")
        .eq("article_id", id)
        .then(({ data }) => {
          if (data) setSelectedWebsiteIds(data.map((d: any) => d.website_id));
        });
    }
  }, [id, isNew]);

  useEffect(() => {
    supabase.from("categories").select("*").order("name").then(({ data }) => {
      setCategories(data || []);
    });
  }, []);

  useEffect(() => {
    if (!isNew && id) {
      tenantDb.from("articles").select("*").eq("id", id).eq("company_id", currentCompanyId).single().then(({ data, error }) => {
        if (data) {
          setArticle({
            title: data.title || "",
            slug: data.slug || "",
            subtitle: data.subtitle || "",
            content: data.content || "",
            excerpt: data.excerpt || "",
            featured_image_url: data.featured_image_url || "",
            category_id: data.category_id || "",
            language: (data as any).language || DEFAULT_LANGUAGE,
            tags: data.tags || [],
            status: data.status || "approved",
            is_breaking: data.is_breaking || false,
            is_featured: data.is_featured || false,
            is_opinion: data.is_opinion || false,
            seo_title: data.seo_title || "",
            seo_description: data.seo_description || "",
          });
        }
      });
    }
  }, [currentCompanyId, id, isNew]);

  const set = (key: string, value: any) => setArticle((a) => ({ ...a, [key]: value }));

  const [qaRunning, setQaRunning] = useState(false);
  const [qaReport, setQaReport] = useState<any>((article as any).qa_report || null);
  useEffect(() => { setQaReport((article as any).qa_report || null); }, [(article as any).qa_report]);
  const runQa = async () => {
    if (!id) return;
    setQaRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("qa-pass", { body: { article_id: id, attempt: Date.now() } });
      if (error) throw new Error((data as any)?.error || error.message);
      if ((data as any)?.error) throw new Error(typeof (data as any).error === "string" ? (data as any).error : (data as any).error.message || "QA failed");
      setQaReport((data as any).report);
    } catch (e: any) {
      sonnerToast.error(e.message);
    } finally {
      setQaRunning(false);
    }
  };

  const handleTitleChange = (title: string) => {
    set("title", title);
    if (isNew) set("slug", slugify(title));
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !article.tags.includes(tag)) {
      set("tags", [...article.tags, tag]);
    }
    setTagInput("");
  };

  const removeTag = (tag: string) => {
    set("tags", article.tags.filter((t) => t !== tag));
  };

  const calculateReadingTime = (text: string) => Math.max(1, Math.ceil(text.split(/\s+/).length / 200));

  const save = async (publish = false) => {
    if (!user || !currentCompanyId) return;
    setSaving(true);

    const status = (publish ? "published" : article.status) as "draft" | "review" | "revision" | "approved" | "scheduled" | "published" | "archived";

    if (!article.language) {
      toast({ title: "Langue requise", description: "Choisissez une langue avant d'enregistrer", variant: "destructive" });
      setSaving(false);
      return;
    }

    // Check article creation limit if creating a new article
    if (isNew && !canCreateArticles) {
      toast({
        title: "Forfait requis",
        description: "Aucun forfait actif n'est lie a cet espace de travail.",
        variant: "destructive",
      });
      setSaving(false);
      return;
    }

    // Check if monthly article limit is reached
    if (isNew && packageInfo?.max_articles_per_month != null) {
      if (usage.articles_created_this_month >= packageInfo.max_articles_per_month) {
        toast({
          title: "Limite mensuelle atteinte",
          description: `Vous avez atteint votre limite de ${packageInfo.max_articles_per_month} articles par mois.`,
          variant: "destructive",
        });
        setSaving(false);
        return;
      }
    }

    const payload = {
      title: article.title,
      slug: article.slug,
      subtitle: article.subtitle || null,
      content: article.content,
      excerpt: article.excerpt || null,
      featured_image_url: article.featured_image_url || null,
      category_id: article.category_id || null,
      language: article.language,
      tags: article.tags,
      status,
      is_breaking: article.is_breaking,
      is_featured: article.is_featured,
      is_opinion: article.is_opinion,
      seo_title: article.seo_title || null,
      seo_description: article.seo_description || null,
      author_id: user.id,
      company_id: currentCompanyId,
      reading_time: calculateReadingTime(article.content),
      ...(publish ? { published_at: new Date().toISOString() } : {}),
    };

    try {
      let articleId = id;
      if (isNew) {
        const { data, error } = await tenantDb.from("articles").insert([payload]).select().single();
        if (error) throw error;
        articleId = data.id;
      } else {
        const { error } = await tenantDb.from("articles").update(payload).eq("id", id!).eq("company_id", currentCompanyId);
        if (error) throw error;
      }

      // Sync article_publications junction table or publish immediately to selected websites
      if (articleId) {
        if (publish && selectedWebsiteIds.length > 0) {
          for (const websiteId of selectedWebsiteIds) {
            const { data, error } = await supabase.functions.invoke("publish-to-wordpress", {
              body: { article_id: articleId, website_id: websiteId, action: "publish" },
            });
            if (error || (data as any)?.error) throw new Error((data as any)?.error || error?.message || "Publication failed");
          }
        } else {
          await supabase.from("article_publications").delete().eq("article_id", articleId);
          const rows = selectedWebsiteIds.map((wId) => ({
            article_id: articleId!,
            website_id: wId,
            status: "draft",
          }));
          if (rows.length > 0) await supabase.from("article_publications").insert(rows);
        }
      }

      toast({ title: publish ? "Article publié !" : isNew ? "Article sauvegardé" : "Modifications sauvegardées" });
      if (isNew) navigate(`/articles/${articleId}`, { replace: true });
    } catch (err: any) {
      toast({ title: "Erreur", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh)] flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-border bg-card px-4 py-3">
        <div className="flex items-center gap-3">
          <IconButton variant="ghost" onClick={() => navigate("/articles")} label={t("actions.back")}>
            <ArrowLeft className="h-4 w-4" />
          </IconButton>
          <span className="text-sm font-medium text-foreground">
            {isNew ? "Nouvel article" : "Modifier l'article"}
          </span>
          <Badge variant="outline" className="text-xs">
            {article.status}
          </Badge>
          {isNew && packageInfo?.max_articles_per_month != null && (
            <div className="ml-4 text-xs text-muted-foreground">
              Articles: {usage.articles_created_this_month}/{packageInfo.max_articles_per_month}
              {usage.articles_created_this_month >= packageInfo.max_articles_per_month && (
                <Badge variant="destructive" className="ml-2">Limite atteinte</Badge>
              )}
              {usage.articles_created_this_month >= (packageInfo.max_articles_per_month * 0.9) && 
               usage.articles_created_this_month < packageInfo.max_articles_per_month && (
                <Badge variant="outline" className="ml-2 bg-amber-100 text-amber-800">Proche de la limite</Badge>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => save(false)} disabled={saving || (isNew && !canCreateArticles) || (isNew && packageInfo?.max_articles_per_month != null && usage.articles_created_this_month >= packageInfo.max_articles_per_month)}>
            <Save className="mr-2 h-3.5 w-3.5" />
            Sauvegarder
          </Button>
          <Button size="sm" onClick={() => save(true)} disabled={saving || (isNew && !canCreateArticles) || (isNew && packageInfo?.max_articles_per_month != null && usage.articles_created_this_month >= packageInfo.max_articles_per_month)}>
            <Send className="mr-2 h-3.5 w-3.5" />
            Publier
          </Button>
        </div>
      </div>

      {/* Editor body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Main editor */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isNew && isTrialPackage && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
              Trial workspaces can review temporary article examples, but new article creation is locked until a paid package is selected.
            </div>
          )}
          <Input
            placeholder="Titre de l'article"
            value={article.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            className="border-none bg-transparent text-3xl font-bold placeholder:text-muted-foreground/40 focus-visible:ring-0 p-0 h-auto"
          />
          <Input
            placeholder="Sous-titre (optionnel)"
            value={article.subtitle}
            onChange={(e) => set("subtitle", e.target.value)}
            className="border-none bg-transparent text-lg text-muted-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 p-0 h-auto"
          />

          {/* Featured image */}
          {article.featured_image_url ? (
            <div className="relative rounded-lg overflow-hidden">
              <img src={article.featured_image_url} alt="" className="w-full h-64 object-cover" />
              <IconButton
                variant="destructive"
                className="absolute top-2 right-2 h-8 w-8"
                onClick={() => set("featured_image_url", "")}
                label={t("actions.removeImage")}
              >
                <X className="h-4 w-4" />
              </IconButton>
            </div>
          ) : (
            <Button variant="outline" className="w-full h-32 border-dashed flex flex-col gap-2" onClick={() => setShowMediaPicker(true)}>
              <ImageIcon className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Choisir une image à la une</span>
            </Button>
          )}
          <MediaPickerDialog
            open={showMediaPicker}
            onOpenChange={setShowMediaPicker}
            onSelect={(url, altText) => {
              set("featured_image_url", url);
              // Auto-fill SEO description with alt text if empty
              if (altText && !article.seo_description) {
                set("seo_description", altText);
              }
            }}
            articleContext={{
              title: article.title,
              tags: article.tags,
              categoryName: categories.find((c) => c.id === article.category_id)?.name || "",
            }}
          />

          {/* Content */}
          <Textarea
            placeholder="Écrivez votre article ici... (Markdown supporté)"
            value={article.content}
            onChange={(e) => set("content", e.target.value)}
            className="min-h-[400px] resize-none border-none bg-transparent text-base leading-relaxed placeholder:text-muted-foreground/30 focus-visible:ring-0 p-0"
          />
        </div>

        {/* Sidebar */}
        <div className="w-80 shrink-0 overflow-y-auto border-l border-border bg-card p-4 space-y-6">
          {/* Slug */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Slug</Label>
            <Input
              value={article.slug}
              onChange={(e) => set("slug", e.target.value)}
              className="text-sm"
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Catégorie</Label>
            <Select value={article.category_id} onValueChange={(v) => set("category_id", v)}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir une catégorie" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Language */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Langue *</Label>
            <Select value={article.language} onValueChange={(v) => set("language", v)}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir une langue" />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => (
                  <SelectItem key={l.code} value={l.code}>{l.flag} {l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground">Utilisée pour le filtrage et la publication multi-langue</p>
          </div>

          {/* Editorial QA (Growth+): second model reads the article and reports. */}
          {id && (
            <div className="space-y-2 rounded-md border border-border p-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Relecture IA</Label>
                <Button size="sm" variant="outline" disabled={qaRunning} onClick={runQa}>{qaRunning ? "…" : "Lancer"}</Button>
              </div>
              {qaReport ? (
                <div className="space-y-1 text-xs">
                  <div>
                    <span className={cn("rounded px-1.5 py-0.5 font-medium", qaReport.verdict === "pass" ? "bg-emerald-500/15 text-emerald-700" : qaReport.verdict === "warn" ? "bg-amber-500/15 text-amber-700" : "bg-red-500/15 text-red-700")}>{qaReport.verdict}</span>
                    <span className="ml-2 text-muted-foreground">ton {qaReport.tone_score}/10 · lisibilité {qaReport.readability_score}/10</span>
                  </div>
                  <p className="text-muted-foreground">{qaReport.summary}</p>
                  {(qaReport.issues || []).slice(0, 6).map((i: any, k: number) => <p key={k}>• <b>{i.severity}</b> {i.category}: {i.note}</p>)}
                  {(qaReport.fact_check_flags || []).slice(0, 4).map((f: string, k: number) => <p key={"f" + k}>⚑ {f}</p>)}
                </div>
              ) : (
                <p className="text-[10px] text-muted-foreground">Signale citations introuvables, contradictions et dérives de style. Coût : crédits du modèle de relecture.</p>
              )}
            </div>
          )}

          {/* Tags */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Tags</Label>
            <div className="flex gap-1 flex-wrap">
              {article.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-xs gap-1">
                  {tag}
                  <button onClick={() => removeTag(tag)}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-1">
              <Input
                placeholder="Ajouter un tag"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                className="text-sm"
              />
              <IconButton variant="outline" onClick={addTag} label={t("actions.addTag")}>
                <Plus className="h-3.5 w-3.5" />
              </IconButton>
            </div>
          </div>

          {/* Excerpt */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Extrait</Label>
            <Textarea
              placeholder="Résumé de l'article..."
              value={article.excerpt}
              onChange={(e) => set("excerpt", e.target.value)}
              className="text-sm"
              rows={3}
            />
          </div>

          {/* Flags */}
          <div className="space-y-3">
            <Label className="text-xs text-muted-foreground">Options</Label>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Breaking News</span>
              <Switch checked={article.is_breaking} onCheckedChange={(v) => set("is_breaking", v)} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Article à la une</span>
              <Switch checked={article.is_featured} onCheckedChange={(v) => set("is_featured", v)} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Opinion</span>
              <Switch checked={article.is_opinion} onCheckedChange={(v) => set("is_opinion", v)} />
            </div>
          </div>

          {/* Publish to Websites */}
          <div className="space-y-2 border-t border-border pt-4">
            <Label className="text-xs text-muted-foreground font-semibold">Publier sur</Label>
            <p className="text-[10px] text-muted-foreground">Sélectionnez les sites où publier cet article</p>
            <Popover open={websitePopoverOpen} onOpenChange={setWebsitePopoverOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-sm">
                  <Globe className="mr-2 h-3.5 w-3.5" />
                  {selectedWebsiteIds.length === 0
                    ? "Choisir des sites…"
                    : `${selectedWebsiteIds.length} site(s) sélectionné(s)`}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0" align="start">
                <Command>
                  <CommandInput placeholder="Rechercher un site…" />
                  <CommandList>
                    <CommandEmpty>Aucun site trouvé</CommandEmpty>
                    <CommandGroup>
                      {websites.map((w: any) => {
                        const selected = selectedWebsiteIds.includes(w.id);
                        return (
                          <CommandItem
                            key={w.id}
                            value={w.name}
                            onSelect={() => {
                              setSelectedWebsiteIds((prev) =>
                                selected ? prev.filter((x) => x !== w.id) : [...prev, w.id]
                              );
                            }}
                          >
                            <Check className={cn("mr-2 h-3.5 w-3.5", selected ? "opacity-100" : "opacity-0")} />
                            <div>
                              <p className="text-sm">{w.name}</p>
                              <p className="text-[10px] text-muted-foreground">{w.domain || "No domain"}</p>
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {selectedWebsiteIds.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {selectedWebsiteIds.map((wId) => {
                  const w = websites.find((x: any) => x.id === wId);
                  return (
                    <Badge key={wId} variant="secondary" className="text-[10px] gap-1">
                      {w?.name || "…"}
                      <button onClick={() => setSelectedWebsiteIds((p) => p.filter((x) => x !== wId))}>
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>

          {/* SEO */}
          <div className="space-y-3 border-t border-border pt-4">
            <Label className="text-xs text-muted-foreground font-semibold">SEO</Label>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Meta titre</Label>
              <Input
                value={article.seo_title}
                onChange={(e) => set("seo_title", e.target.value)}
                placeholder={article.title}
                className="text-sm"
              />
              <p className="text-[10px] text-muted-foreground">{(article.seo_title || article.title).length}/60</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Meta description</Label>
              <Textarea
                value={article.seo_description}
                onChange={(e) => set("seo_description", e.target.value)}
                placeholder="Description pour les moteurs de recherche..."
                className="text-sm"
                rows={2}
              />
              <p className="text-[10px] text-muted-foreground">{article.seo_description.length}/160</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
